import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../components/ui/hover-card';
import { toast } from 'sonner';
import { Plus, Users, Loader2, Mail, Phone, Building2, Linkedin, MapPin, FileSpreadsheet, RefreshCw, RotateCcw, Database, Trash2, PhoneCall, Search, X, MessageSquare, PhoneIncoming, Clock } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import FranchiseImportExportModal from '../../components/FranchiseImportExportModal';
import AdvancedDataTable from '../../components/AdvancedDataTable';
import EmailMergeDialog from '../../components/EmailMergeDialog';
import { LogCallDialog } from '../../components/LogCallDialog';
import ActiveCallPopup from '../../components/ActiveCallPopup';
import { usePermissions, PERMISSIONS } from '../../hooks/usePermissions';
import { useFranchiseTelnyx } from '../../hooks/useFranchiseTelnyx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
  { value: 'negotiating', label: 'Negotiating', color: 'bg-orange-100 text-orange-700' },
  { value: 'won', label: 'Won', color: 'bg-green-100 text-green-700' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700' },
];

const INTEREST_LEVELS = [
  { value: 'high', label: 'High', color: 'bg-green-100 text-green-700' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-700' },
];

const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'interested', label: 'Interested', color: 'bg-green-100 text-green-700' },
  { value: 'not_interested', label: 'Not Interested', color: 'bg-red-100 text-red-700' },
];

const INTEREST_IN_TAGS = [
  { value: 'Franchise', color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  { value: 'JobTalk', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'NotInterested', color: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'Leadership', color: 'bg-purple-100 text-purple-700 border-purple-300' },
];

const initialFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  linkedin_url: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  current_employer: '',
  years_experience: '',
  franchise_interest: 'medium',
  lead_status: 'new',
  interest_in: [],
  lead_owner: '',
  lead_source: '',
  notes: '',
  status: 'new',
  list_ids: [],
  assigned_to: '',
  dnc: false,  // Do Not Call
  dne: false   // Do Not Email
};

// Helper functions for badges - defined outside component to avoid re-creation
const getStatusBadge = (status) => {
  const s = STATUSES.find(x => x.value === status) || STATUSES[0];
  return <Badge className={s.color}>{s.label}</Badge>;
};

const getLeadStatusBadge = (leadStatus) => {
  const s = LEAD_STATUS_OPTIONS.find(x => x.value === leadStatus) || LEAD_STATUS_OPTIONS[0];
  return <Badge className={s.color}>{s.label}</Badge>;
};

const getInterestInTags = (tags) => {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map(tag => {
        const tagConfig = INTEREST_IN_TAGS.find(t => t.value === tag);
        return (
          <Badge key={tag} variant="outline" className={`text-xs ${tagConfig?.color || ''}`}>
            {tag}
          </Badge>
        );
      })}
    </div>
  );
};

// Cell renderers defined outside component
const NameCell = ({ row }) => (
  <div>
    <div className="font-medium text-slate-900">
      {row.original.first_name} {row.original.last_name}
    </div>
    {row.original.linkedin_url && (
      <a 
        href={row.original.linkedin_url} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-xs text-blue-600 flex items-center gap-1 mt-1 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        <Linkedin className="h-3 w-3" /> LinkedIn
      </a>
    )}
  </div>
);

const EmailCell = ({ row }) => (
  row.original.email ? (
    <div className="text-sm text-slate-600 flex items-center gap-1">
      <Mail className="h-3 w-3" /> {row.original.email}
    </div>
  ) : <span className="text-slate-400">—</span>
);

// Clickable phone cell that initiates a call
const PhoneCell = ({ row, onCallClick }) => (
  row.original.phone ? (
    <button 
      onClick={(e) => {
        e.stopPropagation();  // Prevent row click
        if (onCallClick) {
          onCallClick(row.original.phone, row.original);
        }
      }}
      className="text-sm text-slate-600 flex items-center gap-1 hover:text-green-600 hover:bg-green-50 px-2 py-1 rounded transition-colors group"
      title="Click to call"
    >
      <PhoneCall className="h-3 w-3 group-hover:text-green-600" />
      <span>{row.original.phone}</span>
    </button>
  ) : <span className="text-slate-400">—</span>
);

// Activity Hover Cell - shows last 3 activities on hover
const ActivityHoverCell = ({ children, leadId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const fetchActivities = async () => {
    if (fetched || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/franchise/leads/${leadId}/communications`);
      const comms = res.data?.communications || res.data || [];
      // Sort by date descending and take top 3
      const sorted = comms.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 3);
      setActivities(sorted);
      setFetched(true);
    } catch (error) {
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="h-3 w-3 text-blue-500" />;
      case 'sms': return <MessageSquare className="h-3 w-3 text-green-500" />;
      case 'call': return <PhoneIncoming className="h-3 w-3 text-purple-500" />;
      default: return <Clock className="h-3 w-3 text-slate-400" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild onMouseEnter={fetchActivities}>
        <div className="cursor-default">{children}</div>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-3" side="left" align="start">
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-700 border-b pb-1">Recent Activities</p>
          {loading ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
            </div>
          ) : activities.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-2">No recent activities</p>
          ) : (
            <div className="space-y-2">
              {activities.map((activity, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs">
                  <div className="mt-0.5">{getActivityIcon(activity.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-700 capitalize">{activity.type}</p>
                    <p className="text-slate-500 truncate">{activity.subject || activity.notes || activity.outcome || '—'}</p>
                    <p className="text-slate-400 text-[10px]">{formatDate(activity.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const LocationCell = ({ row }) => {
  const location = [row.original.city, row.original.state].filter(Boolean).join(', ');
  return location ? (
    <div className="text-sm text-slate-600 flex items-center gap-1">
      <MapPin className="h-3 w-3" /> {location}
    </div>
  ) : <span className="text-slate-400">—</span>;
};

const EmployerCell = ({ row }) => (
  <div>
    {row.original.current_employer ? (
      <div className="text-sm text-slate-600 flex items-center gap-1">
        <Building2 className="h-3 w-3" /> {row.original.current_employer}
      </div>
    ) : <span className="text-slate-400">—</span>}
    {row.original.years_experience && (
      <div className="text-xs text-slate-500">{row.original.years_experience} yrs exp</div>
    )}
  </div>
);

const LeadStatusCell = ({ row }) => getLeadStatusBadge(row.original.lead_status);
const InterestInCell = ({ row }) => getInterestInTags(row.original.interest_in);
const PipelineStatusCell = ({ row }) => getStatusBadge(row.original.status);

export default function FranchiseLeadsPage() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [leads, setLeads] = useState([]);
  const [lists, setLists] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Permission checks
  const canWriteLeads = hasPermission(PERMISSIONS.FRANCHISE_LEADS_WRITE);
  const canDeleteLeads = hasPermission(PERMISSIONS.FRANCHISE_LEADS_DELETE);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter states
  const [showDNC, setShowDNC] = useState(false); // Show only DNC leads
  const [showDNE, setShowDNE] = useState(false); // Show only DNE leads
  const [selectedInterests, setSelectedInterests] = useState([]); // Multi-select filter for Interest In
  const [interestDropdownOpen, setInterestDropdownOpen] = useState(false);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkEmailDialogOpen, setBulkEmailDialogOpen] = useState(false);
  
  // Table state for reset functionality
  const [tableKey, setTableKey] = useState(0);
  
  // Column order and visibility preferences - persist to localStorage
  const COLUMN_PREFS_KEY = 'franchise_leads_column_prefs';
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem(COLUMN_PREFS_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.order || null;
      } catch { return null; }
    }
    return null;
  });
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(COLUMN_PREFS_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.visibility || {};
      } catch { return {}; }
    }
    return {};
  });
  
  // Use the reusable Telnyx hook for calling functionality
  const {
    telnyxStatus,
    activeCallPopupOpen,
    activeCallStatus,
    activeCallLead,
    callDialogOpen,
    setCallDialogOpen,
    savingCall,
    callForm,
    setCallForm,
    handleClickToCall,
    handleDisconnectCall,
    handleLogCallFromPopup,
    handleLogCallSubmit,
    closeCallPopup,
    closeCallDialog
  } = useFranchiseTelnyx({
    onCallLogged: () => {
      // Refresh data after call is logged
      loadData(false);
    }
  });
  
  // Cache - use sessionStorage for cross-page persistence
  const cacheRef = useRef({ leads: null, lists: null, timestamp: null });
  const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
  
  // Save column preferences
  const saveColumnPrefs = useCallback((order, visibility) => {
    localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify({ order, visibility }));
  }, []);

  useEffect(() => {
    // Try to load from sessionStorage first for faster initial load
    const cachedData = sessionStorage.getItem('franchise_leads_cache');
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        if (Date.now() - parsed.timestamp < CACHE_DURATION) {
          setLeads(parsed.leads || []);
          setLists(parsed.lists || []);
          cacheRef.current = parsed;
          setLoading(false);
          return;
        }
      } catch { /* ignore */ }
    }
    loadData(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async (forceRefresh = false) => {
    const now = Date.now();
    const cache = cacheRef.current;
    
    if (!forceRefresh && cache.leads && cache.timestamp && (now - cache.timestamp < CACHE_DURATION)) {
      setLeads(cache.leads);
      setLists(cache.lists || []);
      setLoading(false);
      return;
    }

    forceRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const [leadsRes, listsRes, usersRes] = await Promise.all([
        axios.get(`${API}/franchise/leads`),
        axios.get(`${API}/franchise/lists`),
        axios.get(`${API}/users`).catch(() => ({ data: [] }))
      ]);
      
      const leadsData = leadsRes.data.items || leadsRes.data.leads || leadsRes.data || [];
      const listsData = listsRes.data.lists || listsRes.data || [];
      
      setLeads(leadsData);
      setLists(listsData);
      setUsers(usersRes.data || []);
      
      const cacheData = { leads: leadsData, lists: listsData, timestamp: now };
      cacheRef.current = cacheData;
      // Also save to sessionStorage for cross-page persistence
      sessionStorage.setItem('franchise_leads_cache', JSON.stringify(cacheData));
      
      if (forceRefresh) {
        toast.success('Data reloaded from database');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied - Franchise module');
        navigate('/app');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Reload from database (clears search, filters and resets table)
  const handleRefresh = () => {
    setSearchQuery('');
    setShowDNC(false);
    setShowDNE(false);
    setSelectedInterests([]);
    setInterestDropdownOpen(false);
    setTableKey(prev => prev + 1);
    loadData(true);
  };
  
  // Reset all filters and sorting (uses cached data)
  const handleReset = () => {
    setSearchQuery('');
    setShowDNC(false);
    setShowDNE(false);
    setSelectedInterests([]);
    setInterestDropdownOpen(false);
    setTableKey(prev => prev + 1); // Force table remount to reset all state
    toast.info('Filters and sorting reset');
  };

  const handleCreate = async () => {
    if (!formData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!formData.email?.trim()) {
      toast.error('Email is required');
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSaving(true);
    try {
      // Clean up data - convert empty strings to null and parse numbers
      const cleanedData = {
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience, 10) : null,
        email: formData.email.trim().toLowerCase(),  // Email is required, normalize to lowercase
        phone: formData.phone?.trim() || null,
        linkedin_url: formData.linkedin_url?.trim() || null,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        zip_code: formData.zip_code?.trim() || null,
        current_employer: formData.current_employer?.trim() || null,
        territory_preference: formData.territory_preference?.trim() || null,
        investment_budget: formData.investment_budget?.trim() || null,
        lead_owner: formData.lead_owner?.trim() || null,
        lead_source: formData.lead_source?.trim() || null,
        notes: formData.notes?.trim() || null,
        assigned_to: formData.assigned_to?.trim() || null,
      };
      await axios.post(`${API}/franchise/leads`, cleanedData);
      toast.success('Lead created');
      setCreateDialogOpen(false);
      setFormData(initialFormData);
      loadData(true);
    } catch (error) {
      // Handle different error response formats
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to create lead';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors come as array
        errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLead) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/franchise/leads/${selectedLead.id}`);
      toast.success('Lead deleted');
      setDeleteDialogOpen(false);
      setSelectedLead(null);
      loadData(true);
    } catch (error) {
      toast.error('Failed to delete lead');
    } finally {
      setSaving(false);
    }
  };

  // Bulk selection functions
  const toggleSelectLead = (leadId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    const allIds = new Set(leads.map(lead => lead.id));
    setSelectedIds(allIds);
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setSaving(true);
    try {
      const deletePromises = Array.from(selectedIds).map(id => 
        axios.delete(`${API}/franchise/leads/${id}`)
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedIds.size} leads deleted`);
      setBulkDeleteDialogOpen(false);
      setSelectedIds(new Set());
      loadData(true);
    } catch (error) {
      toast.error('Failed to delete some leads');
    } finally {
      setSaving(false);
    }
  };

  const toggleInterestTag = (tag) => {
    const current = formData.interest_in || [];
    if (current.includes(tag)) {
      setFormData({ ...formData, interest_in: current.filter(t => t !== tag) });
    } else {
      setFormData({ ...formData, interest_in: [...current, tag] });
    }
  };

  // Filter leads based on search query and DNC/DNE filters
  const filteredLeads = useMemo(() => {
    let result = leads;
    
    // Apply DNC filter
    if (showDNC) {
      result = result.filter(lead => lead.dnc === true);
    }
    
    // Apply DNE filter
    if (showDNE) {
      result = result.filter(lead => lead.dne === true);
    }
    
    // Apply Interest In filter
    if (selectedInterests.length > 0) {
      result = result.filter(lead => {
        const leadInterests = lead.interest_in || [];
        return selectedInterests.some(interest => leadInterests.includes(interest));
      });
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().replace(/[\s\-\(\)\.]/g, ''); // Normalize for search
      
      result = result.filter(lead => {
        // Text search on common fields (use normalized query)
        if ((lead.first_name || '').toLowerCase().includes(q) ||
            (lead.last_name || '').toLowerCase().includes(q) ||
            (lead.email || '').toLowerCase().includes(q) ||
            (lead.current_employer || '').toLowerCase().includes(q) ||
            (lead.city || '').toLowerCase().includes(q) ||
            (lead.state || '').toLowerCase().includes(q)) {
          return true;
        }
        
        // Phone number search - strip non-digits for matching
        const phoneDigits = (lead.phone || '').replace(/\D/g, '');
        const qDigits = q.replace(/\D/g, '');
        if (qDigits && phoneDigits.includes(qDigits)) {
          return true;
        }
        
        return false;
      });
    }
    
    return result;
  }, [leads, searchQuery, showDNC, showDNE, selectedInterests]);

  // Define columns for the data table - uses external cell renderers
  const columns = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          checked={selectedIds.size > 0 && selectedIds.size === leads.length}
          ref={input => {
            if (input) {
              input.indeterminate = selectedIds.size > 0 && selectedIds.size < leads.length;
            }
          }}
          onChange={(e) => {
            if (e.target.checked) {
              selectAll();
            } else {
              deselectAll();
            }
          }}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          checked={selectedIds.has(row.original.id)}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelectLead(row.original.id);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      size: 40,
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      accessorFn: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim(),
      cell: NameCell,
      size: 180,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: EmailCell,
      size: 200,
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ row }) => <PhoneCell row={row} onCallClick={handleClickToCall} />,
      size: 140,
    },
    {
      accessorKey: 'location',
      header: 'Location',
      accessorFn: (row) => [row.city, row.state].filter(Boolean).join(', '),
      cell: LocationCell,
      size: 150,
    },
    {
      accessorKey: 'current_employer',
      header: 'Employer',
      cell: EmployerCell,
      size: 160,
    },
    {
      accessorKey: 'lead_status',
      header: 'Lead Status',
      cell: LeadStatusCell,
      filterFn: 'equals',
      meta: {
        filterVariant: 'select',
        filterOptions: LEAD_STATUS_OPTIONS.map(s => ({ value: s.value, label: s.label })),
      },
      size: 130,
    },
    {
      accessorKey: 'interest_in',
      header: 'Interest In',
      accessorFn: (row) => (row.interest_in || []).join(', '),
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => (
        <ActivityHoverCell leadId={row.original.id}>
          <InterestInCell row={row} />
        </ActivityHoverCell>
      ),
      size: 160,
    },
    {
      accessorKey: 'status',
      header: 'Pipeline Status',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => (
        <ActivityHoverCell leadId={row.original.id}>
          <PipelineStatusCell row={row} />
        </ActivityHoverCell>
      ),
      filterFn: 'equals',
      meta: {
        filterVariant: 'select',
        filterOptions: STATUSES.map(s => ({ value: s.value, label: s.label })),
      },
      size: 140,
    },
    {
      accessorKey: 'lists',
      header: 'Lists',
      accessorFn: (row) => (row.list_ids || []).map(id => {
        const lst = lists.find(l => l.id === id);
        return lst?.name || '';
      }).filter(Boolean).join(', '),
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => {
        const leadLists = (row.original.list_ids || []).map(id => lists.find(l => l.id === id)).filter(Boolean);
        return (
          <ActivityHoverCell leadId={row.original.id}>
            {leadLists.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {leadLists.map(lst => (
                  <Badge key={lst.id} variant="outline" className="text-xs" style={{ borderColor: lst.color, color: lst.color }}>
                    {lst.name}
                  </Badge>
                ))}
              </div>
            ) : <span className="text-slate-400">—</span>}
          </ActivityHoverCell>
        );
      },
      size: 150,
    },
  ], [lists, selectedIds, leads, selectAll, deselectAll, toggleSelectLead]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Toolbar left content for AdvancedDataTable
  const tableToolbarLeft = (
    <div className="flex items-center gap-2">
      {/* Search */}
      <div className="relative w-72">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search by name, email, phone, location..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-10 h-9"
          data-testid="search-franchise-leads"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="h-4 w-4 text-slate-400 hover:text-slate-600" />
          </button>
        )}
      </div>
      {/* Reset */}
      <Button
        variant="outline"
        size="icon"
        onClick={handleReset}
        title="Reset filters (use cached data)"
        className="h-9 w-9"
        data-testid="reset-btn"
      >
        <RotateCcw className="h-4 w-4" />
      </Button>
      {/* Reload */}
      <Button
        variant="outline"
        size="icon"
        onClick={handleRefresh}
        disabled={refreshing}
        title="Reload from database"
        className="h-9 w-9"
        data-testid="reload-btn"
      >
        {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
      </Button>
      {/* DNC and DNE Filters */}
      <div className="flex items-center gap-3 border-l pl-3 ml-1">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Checkbox
            checked={showDNC}
            onCheckedChange={setShowDNC}
            data-testid="dnc-filter"
          />
          <span className="text-sm font-medium text-slate-700">DNC</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Checkbox
            checked={showDNE}
            onCheckedChange={setShowDNE}
            data-testid="dne-filter"
          />
          <span className="text-sm font-medium text-slate-700">DNE</span>
        </label>
      </div>
      
      {/* Interest In Multi-Select Filter */}
      <div className="relative border-l pl-3 ml-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setInterestDropdownOpen(!interestDropdownOpen)}
          className="gap-2 h-9"
          data-testid="interest-filter"
        >
          Interest In
          {selectedInterests.length > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 px-1.5">{selectedInterests.length}</Badge>
          )}
        </Button>
        {interestDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-48 bg-white border rounded-lg shadow-lg z-50 p-2">
            <div className="flex justify-between items-center mb-2 pb-2 border-b">
              <span className="text-xs font-medium text-slate-500">Filter by Interest</span>
              <div className="flex items-center gap-2">
                {selectedInterests.length > 0 && (
                  <button
                    onClick={() => setSelectedInterests([])}
                    className="text-xs text-indigo-600 hover:underline"
                  >
                    Clear
                  </button>
                )}
                <button
                  onClick={() => setInterestDropdownOpen(false)}
                  className="text-slate-400 hover:text-slate-600"
                  data-testid="close-interest-dropdown"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            {INTEREST_IN_TAGS.map(tag => (
              <label
                key={tag.value}
                className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer"
              >
                <Checkbox
                  checked={selectedInterests.includes(tag.value)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedInterests([...selectedInterests, tag.value]);
                    } else {
                      setSelectedInterests(selectedInterests.filter(i => i !== tag.value));
                    }
                  }}
                />
                <Badge variant="outline" className={`text-xs ${tag.color}`}>{tag.value}</Badge>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Franchise Leads</h1>
          <p className="text-slate-500 mt-1">
            {filteredLeads.length} of {leads.length} leads
            {(searchQuery || showDNC || showDNE || selectedInterests.length > 0) && ' (filtered'}
            {showDNC && ' - DNC'}
            {showDNE && ' - DNE'}
            {selectedInterests.length > 0 && ` - ${selectedInterests.length} interests`}
            {(searchQuery || showDNC || showDNE || selectedInterests.length > 0) && ')'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWriteLeads && (
            <Button variant="outline" onClick={() => setCsvImportOpen(true)} data-testid="import-export-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />Import / Export
            </Button>
          )}
          {canWriteLeads && (
            <Button onClick={() => setCreateDialogOpen(true)} data-testid="add-franchise-lead-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Lead
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Selection Controls */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-4 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <span className="text-sm font-medium text-indigo-700">
            {selectedIds.size} lead{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
              Select All ({filteredLeads.length})
            </Button>
            <Button variant="outline" size="sm" onClick={deselectAll} data-testid="deselect-all-btn">
              Deselect All
            </Button>
            <Button variant="default" size="sm" onClick={() => setBulkEmailDialogOpen(true)} data-testid="email-selected-btn">
              <Mail className="h-4 w-4 mr-1" />
              Email Selected
            </Button>
            {canDeleteLeads && (
              <Button variant="destructive" size="sm" onClick={() => setBulkDeleteDialogOpen(true)} data-testid="delete-selected-btn">
                <Trash2 className="h-4 w-4 mr-1" />
                Delete Selected
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {STATUSES.map(s => {
          const count = Array.isArray(leads) ? leads.filter(l => l.status === s.value).length : 0;
          return (
            <Card key={s.value} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-900">{count}</p>
                <Badge className={`${s.color} mt-1`}>{s.label}</Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Advanced Data Table */}
      <Card>
        <CardContent className="p-4">
          <AdvancedDataTable
            key={tableKey}
            data={filteredLeads}
            columns={columns}
            onRowClick={(lead) => navigate(`/admin/franchise/leads/${lead.id}`)}
            pageSize={20}
            enablePrint={true}
            enableExport={true}
            exportFilename="franchise_leads"
            getRowId={(row) => row.id}
            initialColumnVisibility={columnVisibility}
            initialColumnOrder={columnOrder}
            onColumnVisibilityChange={(visibility) => {
              setColumnVisibility(visibility);
              saveColumnPrefs(columnOrder, visibility);
            }}
            onColumnOrderChange={(order) => {
              setColumnOrder(order);
              saveColumnPrefs(order, columnVisibility);
            }}
            hideSearch={true}
            toolbarLeftContent={tableToolbarLeft}
          />
        </CardContent>
      </Card>

      {/* Create Lead Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              Add Franchise Lead
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>First Name *</Label>
              <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} placeholder="John" />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} placeholder="Doe" />
            </div>
            <div>
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@example.com" required />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(555) 123-4567" />
            </div>
            <div className="col-span-2">
              <Label>LinkedIn URL</Label>
              <Input value={formData.linkedin_url} onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})} placeholder="https://linkedin.com/in/..." />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="123 Main St" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} placeholder="New York" />
            </div>
            <div>
              <Label>State</Label>
              <Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} placeholder="NY" />
            </div>
            <div>
              <Label>ZIP Code</Label>
              <Input value={formData.zip_code} onChange={(e) => setFormData({...formData, zip_code: e.target.value})} placeholder="10001" />
            </div>
            <div>
              <Label>Current Employer</Label>
              <Input value={formData.current_employer} onChange={(e) => setFormData({...formData, current_employer: e.target.value})} placeholder="Company Name" />
            </div>
            <div>
              <Label>Years Experience</Label>
              <Input type="number" value={formData.years_experience} onChange={(e) => setFormData({...formData, years_experience: e.target.value})} placeholder="10" />
            </div>
            <div>
              <Label>Franchise Interest</Label>
              <Select value={formData.franchise_interest} onValueChange={(val) => setFormData({...formData, franchise_interest: val})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INTEREST_LEVELS.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lead Status</Label>
              <Select value={formData.lead_status} onValueChange={(val) => setFormData({...formData, lead_status: val})}>
                <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Interest In (select tags)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {INTEREST_IN_TAGS.map(tag => (
                  <button
                    key={tag.value}
                    type="button"
                    onClick={() => toggleInterestTag(tag.value)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      (formData.interest_in || []).includes(tag.value)
                        ? tag.color + ' border-current'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`interest-tag-${tag.value}`}
                  >
                    {tag.value}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Lead Owner</Label>
              <Input value={formData.lead_owner} onChange={(e) => setFormData({...formData, lead_owner: e.target.value})} placeholder="Owner name" />
            </div>
            <div>
              <Label>Lead Source</Label>
              <Input value={formData.lead_source} onChange={(e) => setFormData({...formData, lead_source: e.target.value})} placeholder="e.g., Website, Referral" />
            </div>
            <div>
              <Label>Pipeline Status</Label>
              <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Do Not Call / Do Not Email */}
            <div className="col-span-2 flex items-center gap-6 py-2 border-t border-slate-100 mt-2 pt-4">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="dnc" 
                  checked={formData.dnc} 
                  onCheckedChange={(checked) => setFormData({...formData, dnc: checked})}
                />
                <Label htmlFor="dnc" className="text-sm font-medium cursor-pointer">
                  Do Not Call (DNC)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="dne" 
                  checked={formData.dne} 
                  onCheckedChange={(checked) => setFormData({...formData, dne: checked})}
                />
                <Label htmlFor="dne" className="text-sm font-medium cursor-pointer">
                  Do Not Email (DNE)
                </Label>
              </div>
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Brief notes about this lead..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedLead?.first_name} {selectedLead?.last_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Leads</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.size} selected lead{selectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete {selectedIds.size} Lead{selectedIds.size !== 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import/Export Modal */}
      <FranchiseImportExportModal
        open={csvImportOpen}
        onOpenChange={setCsvImportOpen}
        onImportComplete={() => loadData(true)}
        mode="import"
      />

      {/* Bulk Email Dialog */}
      <EmailMergeDialog
        open={bulkEmailDialogOpen}
        onOpenChange={setBulkEmailDialogOpen}
        selectedLeads={Array.from(selectedIds).map(id => leads.find(l => l.id === id)).filter(Boolean)}
        onSuccess={() => {
          setSelectedIds(new Set());
          setBulkEmailDialogOpen(false);
        }}
      />
      
      {/* Log Call Dialog */}
      <LogCallDialog
        open={callDialogOpen}
        onClose={closeCallDialog}
        lead={activeCallLead}
        callForm={callForm}
        setCallForm={setCallForm}
        onSubmit={handleLogCallSubmit}
        saving={savingCall}
        leadId={activeCallLead?.id}
      />
      
      {/* Active Call Popup */}
      <ActiveCallPopup
        open={activeCallPopupOpen}
        phoneNumber={callForm.phone_number}
        leadName={activeCallLead ? `${activeCallLead.first_name || ''} ${activeCallLead.last_name || ''}`.trim() : 'Unknown'}
        leadCompany={activeCallLead?.current_employer}
        callStatus={activeCallStatus}
        onDisconnect={handleDisconnectCall}
        onClose={closeCallPopup}
        onLogCall={handleLogCallFromPopup}
      />
    </div>
  );
}
